import {
  AddCircleOutline,
  Cancel,
  Check,
  ClearAll,
  Delete,
  Edit,
  List,
  MoreVert,
  NotInterested,
  RadioButtonChecked,
  Save,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAllPrivileges, useContextMenu } from 'kineticUtils';
import { useTranslation } from 'react-i18next';
import { SavedFilter } from './hooks/useSavedFilters';
import { useCombinedTableControlsContext } from './contexts/context';
import { useBoolean } from '@qfa/react-components';
import { useEffect, useRef, useState } from 'react';
import { useSavedFilterEdit } from './hooks/useSavedFilterEdit';
import { useSavedFilterCreate, useSavedOrgFilterCreate } from './hooks/useSavedFilterCreate';
import { FilterInput, KineticModule, PrivilegeType } from '@kinetic-ui/data-layer';
import { useFiltersContext } from './contexts/filtersContext';
import { useSavedFilterContext } from './contexts/savedFiltersContext';

const filterActionMenuItemSx = { gap: 2, padding: 1, paddingLeft: 2, paddingRight: 2 };
const SavedFilterMenuItem = ({
  filter,
  isDefault,
  isActive,
  onClick,
  onRenameClick,
  onSaveClick,
  onDeleteClick,
  canEdit,
}: {
  filter: SavedFilter;
  isDefault?: boolean;
  isActive?: boolean;
  onClick: () => void;
  onRenameClick: () => void;
  onSaveClick: (filterInputs: FilterInput[]) => void;
  onDeleteClick: () => void;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('common');
  const {
    props: { getContentAnchorEl: _, ...menuProps },
    handleOpenMenu,
    handleCloseMenu,
  } = useContextMenu();

  const { savedFilters } = useSavedFilterContext();
  const { filterInputs } = useFiltersContext();

  return (
    <>
      <MenuItem onClick={onClick}>
        <Box sx={{ display: 'inline-flex', gap: 2 }}>
          <span>{filter.name}</span>
          {isActive ? (
            <Tooltip title={t('activeFilterIconTooltip')}>
              <RadioButtonChecked color='primary' fontSize='small' />
            </Tooltip>
          ) : null}
          {isDefault ? (
            <Tooltip title={t('defaultFilterIconTooltip')}>
              <Check fontSize='small' />
            </Tooltip>
          ) : null}
        </Box>
        <Box marginLeft='auto'>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMenu(e);
            }}
          >
            <MoreVert fontSize='small' />
          </IconButton>
        </Box>
      </MenuItem>
      <Menu {...menuProps} MenuListProps={{ disablePadding: true }}>
        {canEdit && (
          <Tooltip title={t('saveFilterTooltip')} placement='left'>
            <MenuItem
              sx={filterActionMenuItemSx}
              onClick={() => {
                onSaveClick(filterInputs);
                handleCloseMenu();
              }}
            >
              <Save fontSize='small' /> {t('save')}
            </MenuItem>
          </Tooltip>
        )}

        <Tooltip
          title={t(isDefault ? 'clearDefaultSavedFilterTooltip' : 'setDefaultSavedFilterTooltip')}
          placement='left'
        >
          <MenuItem
            sx={filterActionMenuItemSx}
            onClick={() => {
              savedFilters.setDefaultSavedFilter(isDefault ? undefined : filter.id);
              handleCloseMenu();
            }}
          >
            {isDefault ? <NotInterested fontSize='small' /> : <Check fontSize='small' />}
            {t(isDefault ? 'removeDefault' : 'setDefault')}
          </MenuItem>
        </Tooltip>

        {canEdit && (
          <Tooltip title={t('editSavedFilterTooltip')} placement='left' onClick={onRenameClick}>
            <MenuItem sx={filterActionMenuItemSx}>
              <Edit fontSize='small' />
              {t('edit')}
            </MenuItem>
          </Tooltip>
        )}
        {canEdit && (
          <Tooltip title={t('deleteSavedFilterTooltip')} placement='left'>
            <MenuItem sx={filterActionMenuItemSx} onClick={onDeleteClick}>
              <Delete fontSize='small' />
              {t('delete')}
            </MenuItem>
          </Tooltip>
        )}
      </Menu>
    </>
  );
};

const MySavedFiltersHeader = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('common');
  return (
    <>
      <Box
        minWidth={350}
        display={'flex'}
        paddingLeft={4}
        paddingRight={4}
        paddingTop={2}
        paddingBottom={2}
        alignItems='center'
      >
        <Typography variant='h5' fontWeight={600}>
          {t('userSavedFiltersTitle')}
        </Typography>

        <Box marginLeft='auto'>
          <Tooltip title={t('addNewSavedFilterTooltip')} placement='left'>
            <IconButton onClick={onClick}>
              <AddCircleOutline fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
    </>
  );
};

const OrganizationSavedFiltersHeader = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('common');
  const isAdmin = useAllPrivileges([PrivilegeType.Admin], KineticModule.System);
  return (
    <>
      <Box
        minWidth={350}
        display={'flex'}
        paddingLeft={4}
        paddingRight={4}
        paddingTop={2}
        paddingBottom={2}
        alignItems='center'
      >
        <Typography variant='h5' fontWeight={600}>
          {t('organizationSavedFiltersTitle')}
        </Typography>

        <Box marginLeft='auto'>
          {isAdmin && (
            <Tooltip title={t('addNewOrganizationSavedFilterTooltip')} placement='left'>
              <IconButton onClick={onClick}>
                <AddCircleOutline fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Divider />
    </>
  );
};

const SavedFiltersList = () => {
  const { t } = useTranslation('common');
  const { savedFilters } = useSavedFilterContext();
  const { editingId, onCancel, onSave, setEditingId } = useSavedFilterEdit();

  const filters = Object.values(savedFilters.value);

  if (!filters.length) {
    return <MenuItem disabled>{t('noUserSavedFilters')}</MenuItem>;
  }

  return (
    <>
      {filters.map((filter) =>
        editingId === filter.id ? (
          <SavedFilterTitleInput
            key={`${filter.id}_rename`}
            onCancel={onCancel}
            onSave={onSave}
            initialValue={filter.name}
            initialColumns={!!filter.columns}
          />
        ) : (
          <SavedFilterMenuItem
            canEdit
            onClick={() => {
              savedFilters.setActiveSavedFilter(filter.id);
            }}
            onSaveClick={(filterInputs) => {
              savedFilters.updateSavedFilter({
                id: filter.id,
                filters: filterInputs,
              });
            }}
            onRenameClick={() => {
              setEditingId(filter.id);
            }}
            onDeleteClick={() => savedFilters.deleteSavedFilter(filter.id)}
            isActive={savedFilters.activeSavedFilterId === filter.id}
            isDefault={savedFilters.defaultSavedFilterId === filter.id}
            key={filter.id}
            filter={filter}
          />
        )
      )}
    </>
  );
};

const OrgSavedFiltersList = () => {
  const { t } = useTranslation('common');
  const { savedFilters } = useSavedFilterContext();
  const { editingId, onCancel, onSave, setEditingId } = useSavedFilterEdit();
  const isAdmin = useAllPrivileges([PrivilegeType.Admin], KineticModule.System);
  const filters = Object.values(savedFilters.orgValue);
  if (!filters.length) {
    return <MenuItem disabled>{t('noOrganizationSavedFilters')}</MenuItem>;
  }

  return (
    <>
      {filters.map((filter) =>
        editingId === filter.id ? (
          <SavedFilterTitleInput
            key={`${filter.id}_rename`}
            onCancel={onCancel}
            onSave={onSave}
            initialValue={filter.name}
            initialColumns={!!filter.columns}
          />
        ) : (
          <SavedFilterMenuItem
            canEdit={isAdmin}
            onClick={() => {
              savedFilters.setActiveSavedFilter(filter.id);
            }}
            onSaveClick={(filterInputs) => {
              savedFilters.updateOrgSavedFilter({
                id: filter.id,
                filters: filterInputs,
              });
            }}
            onRenameClick={() => {
              setEditingId(filter.id);
            }}
            onDeleteClick={() => savedFilters.deleteOrgSavedFilter(filter.id)}
            isActive={savedFilters.activeSavedFilterId === filter.id}
            isDefault={savedFilters.defaultSavedFilterId === filter.id}
            key={filter.id}
            filter={filter}
          />
        )
      )}
    </>
  );
};

const SavedFilterTitleInput = ({
  onCancel,
  onSave,
  initialValue,
  initialColumns,
}: {
  initialValue?: string;
  initialColumns?: boolean;
  onCancel: () => void;
  onSave: (options: { title: string; includeColumns: boolean }) => void;
}) => {
  const { t } = useTranslation('common');
  const [value, setValue] = useState(initialValue ?? '');
  const [includeColumns, setIncludeColumns] = useState(initialColumns ?? true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <MenuItem
      style={{ height: 64 }}
      disableRipple
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      <Box flex={1}>
        <Box width='100%' display='inline-flex' flexDirection='column'>
          <Input
            defaultValue={value}
            inputRef={inputRef}
            style={{ flex: '1 0 auto' }}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSave({ title: value, includeColumns });
              } else if (e.key === 'Escape') {
                onCancel();
              }
            }}
            onChange={(e) => setValue(e.target.value)}
          />

          <FormControlLabel
            slotProps={{ typography: { fontSize: 12 } }}
            style={{ marginLeft: 0 }}
            onClick={(e) => e.stopPropagation()}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSave({ title: value, includeColumns });
              } else if (e.key === 'Escape') {
                onCancel();
              }
            }}
            control={
              <Checkbox
                style={{ padding: 4 }}
                onClick={(e) => e.stopPropagation()}
                size='small'
                id='includeColumns'
                checked={includeColumns}
                onChange={(_, checked) => setIncludeColumns(checked)}
              />
            }
            label={t('includeColumnsLabel')}
          />
        </Box>
      </Box>

      <Tooltip title={t('save')}>
        <span>
          <IconButton
            size='small'
            disabled={!value.length}
            onClick={() => onSave({ title: value, includeColumns })}
          >
            <Save fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('cancel')}>
        <IconButton size='small' onClick={onCancel}>
          <Cancel fontSize='small' />
        </IconButton>
      </Tooltip>
    </MenuItem>
  );
};

export const SavedFiltersButton = () => {
  const { filters } = useCombinedTableControlsContext();
  const { form, populateForm } = useFiltersContext();
  const { savedFilters } = useSavedFilterContext();
  const { t } = useTranslation('common');
  const [{ off, on }, isNewOpen] = useBoolean();
  const [{ off: orgOff, on: orgOn }, isNewOrgOpen] = useBoolean();
  const { onSave } = useSavedFilterCreate({ onComplete: off });
  const { onSave: orgOnSave } = useSavedOrgFilterCreate({ onComplete: orgOff });

  const {
    handleOpenMenu,
    anchorEl,
    props: { getContentAnchorEl: _, ...props },
  } = useContextMenu();

  useEffect(() => {
    if (!anchorEl && isNewOpen) {
      // reset new filter state if menu closes
      off();
    }
  }, [anchorEl, isNewOpen, off]);

  const clearFilters = () => {
    const [, setValues] = form.values;

    setValues({});
    populateForm([]);
    savedFilters.setActiveSavedFilter(undefined);
  };

  if (!filters) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('clearAllFiltersTooltip')}>
        <IconButton size='small' onClick={clearFilters}>
          <ClearAll fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('savedFilters')}>
        <IconButton size='small' onClick={handleOpenMenu}>
          <List fontSize='small' />
        </IconButton>
      </Tooltip>
      <Menu {...props} keepMounted={false} MenuListProps={{ disablePadding: true }}>
        <MySavedFiltersHeader onClick={on} />
        <SavedFiltersList />
        {isNewOpen && <SavedFilterTitleInput onCancel={off} onSave={onSave} />}
        <OrganizationSavedFiltersHeader onClick={orgOn} />
        <OrgSavedFiltersList />
        {isNewOrgOpen && <SavedFilterTitleInput onCancel={orgOff} onSave={orgOnSave} />}
      </Menu>
    </>
  );
};
